import React, { useEffect, useRef, useState } from "react";
import { STEPS, rowsForScale, toggleStep, emptyPhrase } from "../../lib/scaleCountry";
import { playTone } from "../../lib/scaleCountryAudio";

const STEP_MS = 280;

/* ============================================================
   PhraseGrid — a 16-step piano-roll locked to the current scale.
   Rows come from rowsForScale (descending, so the grid reads top
   note → root going down, like sheet music laid on its side).
   ============================================================ */

const PhraseGrid = ({ semis, phrase, onChange }) => {
  const rows = rowsForScale(semis);
  const [playing, setPlaying] = useState(false);
  const [activeCol, setActiveCol] = useState(-1);
  const intervalRef = useRef(null);
  const phraseRef = useRef(phrase);
  phraseRef.current = phrase;

  useEffect(() => () => clearInterval(intervalRef.current), []);

  const stop = () => {
    clearInterval(intervalRef.current);
    intervalRef.current = null;
    setPlaying(false);
    setActiveCol(-1);
  };

  const soundColumn = (col) => {
    (phraseRef.current[col] || []).forEach((midi) =>
      playTone(midi, { durMs: STEP_MS - 30, gain: 0.13 })
    );
  };

  const play = () => {
    let i = 0;
    setPlaying(true);
    setActiveCol(0);
    soundColumn(0);
    intervalRef.current = setInterval(() => {
      i = (i + 1) % STEPS;
      setActiveCol(i);
      soundColumn(i);
    }, STEP_MS);
  };

  const cellClick = (stepIndex, midi) => {
    const wasActive = (phrase[stepIndex] || []).includes(midi);
    onChange(toggleStep(phrase, stepIndex, midi));
    if (!wasActive) playTone(midi, { durMs: 350, gain: 0.13 });
  };

  return (
    <div>
      <div className="overflow-x-auto">
        <div className="inline-block">
          {rows.map((row) => (
            <div key={row.midi} className="flex">
              <div className="w-14 shrink-0 py-1 pr-2 text-right font-mono text-[10px] text-[#97A18B]">
                {row.label}
              </div>
              {Array.from({ length: STEPS }).map((_, stepIndex) => {
                const active = (phrase[stepIndex] || []).includes(row.midi);
                const isBar = stepIndex % 4 === 0 && stepIndex !== 0;
                const isCurrent = playing && activeCol === stepIndex;
                return (
                  <button
                    key={stepIndex}
                    type="button"
                    aria-label={`step ${stepIndex + 1}, ${row.label}${
                      active ? ", on" : ""
                    }`}
                    onClick={() => cellClick(stepIndex, row.midi)}
                    className={`h-6 w-6 shrink-0 border-b border-r border-[#39432F] ${
                      isBar ? "border-l-2 border-l-[#45523F]" : ""
                    } ${active ? "bg-[#A6C46A]" : "bg-[#222B20]"} ${
                      isCurrent ? "outline outline-2 outline-[#E0A458]" : ""
                    }`}
                  />
                );
              })}
            </div>
          ))}
        </div>
      </div>
      <div className="mt-3 flex gap-2">
        <button
          type="button"
          onClick={playing ? stop : play}
          className="rounded-md border border-[#45523F] px-3 py-1.5 font-mono text-xs uppercase tracking-wide hover:border-[#A6C46A]"
        >
          {playing ? "STOP" : "PLAY"}
        </button>
        <button
          type="button"
          onClick={() => {
            stop();
            onChange(emptyPhrase());
          }}
          className="rounded-md border border-[#45523F] px-3 py-1.5 font-mono text-xs uppercase tracking-wide hover:border-[#A6C46A]"
        >
          CLEAR
        </button>
      </div>
    </div>
  );
};

export default PhraseGrid;
